import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Upload, FileText, CheckCircle, XCircle, AlertTriangle, Download } from 'lucide-react';
import { toast } from 'sonner';
import Papa from 'papaparse';
import { parseDecimal } from '@/lib/numberParsing';

export interface CsvPositionSizeConfigRow {
  id: string;
  positionSize?: number;
  positionSizeMultiplier?: number;
  calculatedMultiplier?: number;
  matchedFile?: string;
  status: 'matched' | 'not_matched' | 'invalid';
  errorMessage?: string;
}

interface CsvPositionSizeConfigUploadProps {
  files: Array<{ id: string; filename: string; data?: any[] }>;
  onApplyMultipliers: (multipliers: Map<string, number>) => void;
}

export const CsvPositionSizeConfigUpload: React.FC<CsvPositionSizeConfigUploadProps> = ({
  files,
  onApplyMultipliers
}) => {
  const [isUploading, setIsUploading] = useState(false);
  const [parsedConfig, setParsedConfig] = useState<CsvPositionSizeConfigRow[]>([]);

  // Extract symbol from filename (e.g., "ADAUSDT" from "ADAUSDT.P-1.7-10S")
  const extractSymbol = (str: string): string => {
    // Remove common file extensions
    const cleanStr = str.replace(/\.(xlsx|csv|txt).*$/i, '');
    // Extract the main symbol before any dash or period followed by numbers
    const match = cleanStr.match(/^([A-Z]+(?:\.[A-Z]+)?)/i);
    return match ? match[1].toUpperCase() : cleanStr.toUpperCase();
  };

  // Normalize for basic matching (keep periods and dashes with numbers)
  const normalizeBasic = (str: string): string => {
    return str
      .toLowerCase()
      .replace(/\s+/g, '')
      .trim();
  };

  // Find matching file by ID with multiple strategies
  const findMatchingFileByName = (id: string): typeof files[0] | undefined => {
    console.log('\n🔍 Matching CSV ID:', id);
    console.log('📁 Available files:', files.map(f => f.filename));
    
    // Strategy 1: Exact match (case-insensitive)
    let match = files.find(f => {
      const isMatch = f.filename.toLowerCase() === id.toLowerCase();
      if (isMatch) console.log('✅ Exact match found:', f.filename);
      return isMatch;
    });
    if (match) return match;
    
    // Strategy 2: Normalized match (remove spaces but keep special chars)
    const normalizedId = normalizeBasic(id);
    match = files.find(f => {
      const normalizedFilename = normalizeBasic(f.filename);
      const isMatch = normalizedFilename === normalizedId;
      if (isMatch) console.log('✅ Normalized match found:', f.filename);
      return isMatch;
    });
    if (match) return match;
    
    // Strategy 3: ID is contained in filename
    match = files.find(f => {
      const isMatch = f.filename.toLowerCase().includes(id.toLowerCase());
      if (isMatch) console.log('✅ Partial match (ID in filename):', f.filename);
      return isMatch;
    });
    if (match) return match;
    
    // Strategy 4: Filename is contained in ID
    match = files.find(f => {
      const isMatch = id.toLowerCase().includes(f.filename.toLowerCase());
      if (isMatch) console.log('✅ Partial match (filename in ID):', f.filename);
      return isMatch;
    });
    if (match) return match;
    
    // Strategy 5: Symbol-based matching (e.g., ADAUSDT from ADAUSDT.P-1.7-10S)
    const idSymbol = extractSymbol(id);
    match = files.find(f => {
      const fileSymbol = extractSymbol(f.filename);
      const isMatch = idSymbol === fileSymbol;
      if (isMatch) console.log('✅ Symbol match found:', f.filename, `(${fileSymbol})`);
      return isMatch;
    });
    if (match) return match;
    
    console.log('❌ No match found for:', id);
    return undefined;
  };

  // Find minimum position size in a file's data
  const findMinPositionSize = (fileData: any[]): number => {
    if (!fileData || fileData.length === 0) {
      console.warn('⚠️ No file data provided');
      return 0;
    }
    
    console.log('🔍 Finding min position size in file data. Sample row keys:', fileData[0] ? Object.keys(fileData[0]) : 'No data');
    
    // Expanded list of possible position size column names
    const positionKeys = [
      'Position size (qty)', 
      'Position size qty', 
      'Position size', 
      'Position Size',
      'position_size',
      'position size',
      'Qty', 
      'qty',
      'Volume',
      'volume',
      'Lot',
      'lot',
      'Size',
      'size',
      'Quantity',
      'quantity'
    ];
    
    const positionSizes: number[] = [];
    
    for (const row of fileData) {
      // Try exact key matches first
      for (const key of positionKeys) {
        if (key in row) {
          const value = row[key];
          if (value !== undefined && value !== null && value !== '') {
            const parsed = parseDecimal(value);
            if (!isNaN(parsed) && parsed > 0) {
              positionSizes.push(parsed);
              break; // Found value for this row
            }
          }
        }
      }
      
      // If not found, try case-insensitive matching
      if (positionSizes.length === 0 || positionSizes.length < fileData.indexOf(row) + 1) {
        const rowKeys = Object.keys(row);
        for (const rowKey of rowKeys) {
          const lowerRowKey = rowKey.toLowerCase();
          // Check if any position key matches (case-insensitive)
          if (positionKeys.some(pk => pk.toLowerCase() === lowerRowKey)) {
            const value = row[rowKey];
            if (value !== undefined && value !== null && value !== '') {
              const parsed = parseDecimal(value);
              if (!isNaN(parsed) && parsed > 0) {
                positionSizes.push(parsed);
                break;
              }
            }
          }
        }
      }
    }
    
    if (positionSizes.length === 0) {
      console.warn('⚠️ No position sizes found. Available columns:', fileData[0] ? Object.keys(fileData[0]) : 'No data');
      return 0;
    }
    
    const minSize = Math.min(...positionSizes);
    console.log(`✅ Found ${positionSizes.length} position sizes. Min: ${minSize}`);
    return minSize;
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.csv')) {
      toast.error('Please upload a CSV file');
      return;
    }

    setIsUploading(true);

    try {
      Papa.parse(file, {
        header: true,
        skipEmptyLines: true,
        complete: (results) => {
          try {
            const parsed: CsvPositionSizeConfigRow[] = [];
            let hasMultiplierColumn = false;
            let hasPositionSizeColumn = false;

            // Check which columns are present (case-insensitive)
            if (results.data.length > 0) {
              const firstRow = results.data[0] as any;
              const columnNames = Object.keys(firstRow);
              const lowerColumnNames = columnNames.map(k => k.toLowerCase());
              
              console.log('📊 CSV Columns detected:', columnNames);
              
              // Check for Multiplier column (exact match preferred)
              hasMultiplierColumn = lowerColumnNames.some(col => 
                col === 'multiplier' || 
                col === 'position size multiplier' || 
                col === 'position_size_multiplier' ||
                col.includes('multiplier')
              );
              
              // Check for Position Size column (exact match preferred)
              hasPositionSizeColumn = lowerColumnNames.some(col => 
                col === 'position size' || 
                col === 'position_size' || 
                col === 'positionsize' ||
                (col.includes('position') && col.includes('size'))
              );
              
              console.log('✅ Column detection results:', { 
                hasMultiplierColumn, 
                hasPositionSizeColumn,
                detectedColumns: columnNames
              });
            }

            for (const row of results.data as any[]) {
              // Parse ID
              let id = row['ID'] || row['id'] || row['File'] || row['file'] || '';
              
              // Sanitize ID
              if (id) {
                id = id
                  .replace(/[^\x20-\x7E]/g, '')
                  .replace(/\s*-\s*/g, '-')
                  .replace(/\s+/g, ' ')
                  .trim();
              }
              
              if (!id) continue;

              // Parse values based on available columns
              let multiplier: number | undefined;
              let positionSize: number | undefined;
              let calculatedMultiplier: number | undefined;
              let status: 'matched' | 'not_matched' | 'invalid' = 'not_matched';
              let errorMessage: string | undefined;

              // Find matching file by ID
              const matchedFile = findMatchingFileByName(id);
              
              // If multiplier column exists, use it directly
              if (hasMultiplierColumn) {
                // Try multiple column name variations
                const multiplierRaw = (row['Position Size Multiplier'] || row['position_size_multiplier'] || 
                   row['Multiplier'] || row['multiplier'] || '').toString();
                console.log('📊 Parsing Multiplier from CSV:', multiplierRaw);
                const multiplierValue = parseDecimal(multiplierRaw);
                console.log('  ✅ Multiplier parsed:', multiplierValue);
                
                if (!isNaN(multiplierValue) && multiplierValue > 0) {
                  multiplier = multiplierValue;
                  calculatedMultiplier = multiplierValue;
                  status = matchedFile ? 'matched' : 'not_matched';
                }
              }

              // If position size column exists and no multiplier, calculate multiplier
              if (!multiplier && hasPositionSizeColumn) {
                // Try multiple column name variations (case-insensitive, looking for "Position Size")
                let positionSizeValue: number | undefined;
                const rowKeys = Object.keys(row);
                
                console.log('🔍 Looking for Position Size in columns:', rowKeys);
                
                // Try exact matches first (case-insensitive)
                const exactMatches = ['Position Size', 'position size', 'position_size', 'PositionSize'];
                for (const exactKey of exactMatches) {
                  const foundKey = rowKeys.find(k => k.toLowerCase() === exactKey.toLowerCase());
                  if (foundKey) {
                    const rawValue = row[foundKey];
                    console.log('📊 Parsing Position Size from CSV:', rawValue);
                    const val = parseDecimal(rawValue);
                    console.log('  ✅ Position Size parsed:', val);
                    if (!isNaN(val) && val > 0) {
                      positionSizeValue = val;
                      console.log('✅ Found Position Size via exact match:', foundKey, '=', val);
                      break;
                    }
                  }
                }
                
                // If not found, try partial match
                if (!positionSizeValue) {
                  for (const key of rowKeys) {
                    const lowerKey = key.toLowerCase();
                    if (lowerKey.includes('position') && lowerKey.includes('size')) {
                      const val = parseDecimal(row[key]);
                      if (!isNaN(val) && val > 0) {
                        positionSizeValue = val;
                        console.log('✅ Found Position Size via partial match:', key, '=', val);
                        break;
                      }
                    }
                  }
                }
                
                if (positionSizeValue && positionSizeValue > 0) {
                  positionSize = positionSizeValue;
                  
                  if (matchedFile?.data) {
                    const minSize = findMinPositionSize(matchedFile.data);
                    console.log(`File "${matchedFile.filename}" min position size:`, minSize);
                    
                    if (minSize > 0) {
                      calculatedMultiplier = positionSizeValue / minSize;
                      status = 'matched';
                      console.log(`Calculated multiplier: ${positionSizeValue} / ${minSize} = ${calculatedMultiplier}`);
                    } else {
                      errorMessage = 'Could not find position sizes in file';
                      status = 'invalid';
                    }
                  } else {
                    errorMessage = 'No matching file found';
                    status = 'not_matched';
                  }
                }
              }

              // Final validation with relaxed range
              if (!calculatedMultiplier) {
                status = 'invalid';
                errorMessage = errorMessage || 'Invalid multiplier or position size';
              } else if (calculatedMultiplier < 0.0001 || calculatedMultiplier > 1000) {
                status = 'invalid';
                errorMessage = `Multiplier out of range: ${calculatedMultiplier.toFixed(4)}x (must be 0.0001-1000x)`;
              } else if (status === 'not_matched' && matchedFile) {
                status = 'matched';
              }

              parsed.push({
                id,
                positionSize,
                positionSizeMultiplier: multiplier,
                calculatedMultiplier,
                matchedFile: matchedFile?.filename,
                status,
                errorMessage
              });
            }

            if (parsed.length === 0) {
              toast.error('No valid configuration rows found');
              return;
            }

            setParsedConfig(parsed);
            
            const matchedCount = parsed.filter(p => p.status === 'matched').length;
            const invalidCount = parsed.filter(p => p.status === 'invalid').length;
            
            if (matchedCount > 0) {
              toast.success(`Loaded ${matchedCount} configurations${invalidCount > 0 ? ` (${invalidCount} invalid)` : ''}`);
            } else {
              toast.warning(`Loaded ${parsed.length} configurations but none matched files`);
            }
            
          } catch (error) {
            console.error('Error parsing CSV config:', error);
            toast.error('Failed to parse CSV configuration file');
          } finally {
            setIsUploading(false);
          }
        },
        error: (error) => {
          console.error('Papa parse error:', error);
          toast.error('Failed to read CSV file');
          setIsUploading(false);
        }
      });
    } catch (error) {
      console.error('File upload error:', error);
      toast.error('Failed to upload file');
      setIsUploading(false);
    }
  };

  const handleApply = () => {
    const validConfigs = parsedConfig.filter(c => c.status === 'matched' && c.calculatedMultiplier);
    
    if (validConfigs.length === 0) {
      toast.error('No valid configurations to apply');
      return;
    }

    const multipliers = new Map<string, number>();
    
    for (const config of validConfigs) {
      const matchedFile = findMatchingFileByName(config.id);
      
      if (matchedFile && config.calculatedMultiplier) {
        multipliers.set(matchedFile.id, config.calculatedMultiplier);
        console.log(`Applying multiplier ${config.calculatedMultiplier}x to file:`, matchedFile.filename);
      }
    }

    onApplyMultipliers(multipliers);
    toast.success(`Applied multipliers to ${multipliers.size} files`);
  };

  const downloadTemplate = (type: 'direct' | 'position') => {
    let csvContent = '';
    
    if (type === 'direct') {
      csvContent = 'ID,Position Size Multiplier\n';
      csvContent += files.slice(0, 3).map(f => `${f.filename},1.5`).join('\n');
    } else {
      csvContent = 'ID,Position Size\n';
      csvContent += files.slice(0, 3).map(f => {
        const minSize = f.data ? findMinPositionSize(f.data) : 100;
        return `${f.filename},${minSize}`;
      }).join('\n');
    }

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `position_size_config_template_${type}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast.success(`Downloaded ${type === 'direct' ? 'Direct Multiplier' : 'Position Size Based'} template`);
  };

  return (
    <Card className="w-full">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <FileText className="h-5 w-5" />
          CSV Adjust Position Size
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="csv-position-size-upload">Upload Position Size Configuration CSV</Label>
          <div className="flex items-center gap-2">
            <Input
              id="csv-position-size-upload"
              type="file"
              accept=".csv"
              onChange={handleFileUpload}
              disabled={isUploading}
              className="flex-1"
            />
            <Button
              variant="outline"
              size="sm"
              disabled={isUploading}
              onClick={() => document.getElementById('csv-position-size-upload')?.click()}
            >
              <Upload className="h-4 w-4 mr-2" />
              {isUploading ? 'Uploading...' : 'Browse'}
            </Button>
          </div>
          <p className="text-xs text-muted-foreground">
            Format: ID | Position Size Multiplier OR ID | Position Size
          </p>
        </div>

        {/* Download Template Buttons */}
        <div className="space-y-2">
          <Label>Download Template</Label>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('direct')}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Direct Multiplier
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => downloadTemplate('position')}
              className="flex-1"
            >
              <Download className="h-4 w-4 mr-2" />
              Position Size Based
            </Button>
          </div>
        </div>

        {parsedConfig.length > 0 && (
          <>
            <div className="space-y-2">
              <Label>Configuration Preview ({parsedConfig.length} entries)</Label>
              <div className="max-h-60 overflow-y-auto border rounded-md">
                <table className="w-full text-xs">
                  <thead className="bg-muted/50 sticky top-0">
                    <tr>
                      <th className="text-left p-2 border-b">ID</th>
                      <th className="text-left p-2 border-b">Matched File</th>
                      <th className="text-right p-2 border-b">CSV Input</th>
                      <th className="text-right p-2 border-b">Multiplier</th>
                      <th className="text-left p-2 border-b">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedConfig.map((row, index) => (
                      <tr key={index} className="border-b">
                        <td className="p-2 font-mono text-xs">{row.id}</td>
                        <td className="p-2 text-xs truncate max-w-[200px]">
                          {row.matchedFile || '-'}
                        </td>
                        <td className="p-2 text-right">
                          {row.positionSize 
                            ? `${row.positionSize} (size)` 
                            : row.positionSizeMultiplier 
                            ? `${row.positionSizeMultiplier}x` 
                            : '-'}
                        </td>
                        <td className="p-2 text-right font-medium">
                          {row.calculatedMultiplier 
                            ? `${row.calculatedMultiplier.toFixed(2)}x` 
                            : '-'}
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            {row.status === 'matched' ? (
                              <CheckCircle className="h-3 w-3 text-primary" />
                            ) : row.status === 'invalid' ? (
                              <XCircle className="h-3 w-3 text-destructive" />
                            ) : (
                              <AlertTriangle className="h-3 w-3 text-warning" />
                            )}
                            {row.errorMessage && (
                              <span className="text-xs text-muted-foreground">{row.errorMessage}</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="space-y-3">
              <div className="p-3 bg-primary/5 border border-primary/20 rounded-md">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="h-4 w-4 text-primary mt-0.5 flex-shrink-0" />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-primary">
                      How Position Size Calculation Works:
                    </p>
                    <p className="text-xs text-primary/80">
                      • <strong>Direct Multiplier</strong>: Use "Position Size Multiplier" column with values like 1.5, 2.0<br/>
                      • <strong>Position Size Based</strong>: Use "Position Size" column with actual sizes<br/>
                      • Multiplier = (Min Position Size in File) / (Your Position Size)<br/>
                      • Example: File has min 100 lot, you enter 20 → Multiplier = 100/20 = 5.0x<br/>
                      • All positions in that file will be multiplied by 5.0x
                    </p>
                  </div>
                </div>
              </div>

              <Button 
                onClick={handleApply}
                disabled={parsedConfig.filter(c => c.status === 'matched').length === 0}
                className="w-full"
              >
                Apply Multipliers to {parsedConfig.filter(c => c.status === 'matched').length} Matched Files
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
};
